import React, {
  ComponentProps,
  useContext,
  useImperativeHandle,
  useRef,
} from "react";
import { Map } from "react-map-gl/mapbox-legacy";
import { useIsScreenSize } from "../../../hooks/useWindowSize";
import { buildLoginURLWithPrevLocation } from "../../../utils/params";
import { MapToolProvider } from "../MapTools/MapToolContext";
import { MapViewportContext } from "../utils/MapViewportContext";
import {
  BottomRightMapOverlay,
  CenterMapOverlay,
  LeftPanelOverlay,
  MapboxControlsWrapper,
  RightPanelOverlay,
  Shader,
  TopLeftMapOverlay,
  TopRightMapOverlay,
} from "./__styles__/LayeredMap";
import useAccountBoundaryHook from "./accountBoundaryLayer";
import useCbrsHook from "./cbrsLayer";
import useClickObjectMenuHook from "./clickObjectMenuLayer";
import useContourHook from "./contourLayer";
import useCustomMapHook from "./customMapLayer";
import useFirmHook from "./firmLayer";
import useLayeredMapHook from "./layeredMapHook";
import useMarkerHook from "./markerLayer";
import useMeasureHook from "./measureLayer";
import { MobileBottomTray } from "./MobileBottomTray/MobileBottomTray";
import useParcelHook from "./parcelLayer";
import usePropertyHook from "./propertyLayer";
import useRasterHook from "./rasterLayer";
import useSavedViewsClusterHook from "./savedViewsClusterLayer";
import useSavedViewsHook from "./savedViewsLayer";
import useSearchResultsLayerHook from "./searchResultsLayer";
import TooltipComponent from "./Tooltip";
import type {
  Account,
  CurriedHookBuilder,
  LayerConfigs,
  LayerHookBuilder,
  MapRef,
  ParentLayerNames,
} from "./types";
import useDeviceLocationsHook from "./userDeviceLocationsLayer";

const layerHookBuilders = {
  parcels: useParcelHook,
  marker: useMarkerHook,
  accountBoundary: useAccountBoundaryHook,
  properties: usePropertyHook,
  firms: useFirmHook,
  customMaps: useCustomMapHook,
  savedViews: useSavedViewsHook,
  savedViewsCluster: useSavedViewsClusterHook,
  cbrs: useCbrsHook,
  contour: useContourHook,
  measure: useMeasureHook,
  raster: useRasterHook,
  deviceLocations: useDeviceLocationsHook,
  clickObjectMenu: useClickObjectMenuHook,
  searchResults: useSearchResultsLayerHook,
};

type Props = {
  layers: LayerConfigs;
  baseMapStyle: string | mapboxgl.Style;
  height?: string;
  width: string;
  minZoom?: number;
  account: Account;
  halfMap?: boolean;
  topLeftMapOverlayItems?: Array<React.ReactNode>;
  topRightMapOverlayItems?: Array<React.ReactNode>;
  centerMapOverlayItems?: Array<React.ReactNode>;
  rightPanelOverlayItems?: Array<React.ReactNode>;
  leftPanelOverlayItems?: Array<React.ReactNode>;
  bottomRightMapOverlayItems?: Array<React.ReactNode>;
  shader?: { hasFeaturedContent: boolean };
} & Omit<ComponentProps<typeof Map>, "children">;

export const LayeredMap = (props: Props) => {
  const { initialViewport, onMove, mapRef } = useContext(MapViewportContext);

  // ideally, we would use useComposedRefs here, but Map internally expects a
  // ref object (with a .current property), not a callback ref. instead, we
  // declare an internal ref to pass to the component and then make that ref
  // available on _this_ component via useImperativeHandle
  const ref = useRef<MapRef | null>(null);
  useImperativeHandle(mapRef, () => ref.current!);

  const hookBuilders = Object.entries(props.layers).map(([layerId, config]) => {
    const layerHookBuilder = layerHookBuilders[
      layerId as ParentLayerNames
    ] as LayerHookBuilder<typeof config>;
    const builder: CurriedHookBuilder = callback =>
      callback(layerHookBuilder, config);
    return builder;
  });

  const {
    onClick,
    onHover,
    onLoad,
    onFocusLost,
    tooltip,
    layers,
    cursor,
    interactiveLayerIds,
    onLoadError,
  } = useLayeredMapHook({
    account: props.account,
    hookBuilders,
    map: ref,
  });

  const isDesktop = useIsScreenSize("desktop");
  const rightPanelOpen = isDesktop && props.rightPanelOverlayItems?.length;

  // Canvas and map containers should not be focusable for keyboard users
  // Instead, we want to direct them towards search functionality
  const removeCanvasFocus = () => {
    const mapInstance = ref.current?.getMap();

    if (mapInstance) {
      const canvasContainer = mapInstance.getCanvasContainer();
      if (canvasContainer) {
        canvasContainer.setAttribute("tabindex", "-1");
      }

      const canvas = mapInstance.getCanvas();
      if (canvas) {
        canvas.setAttribute("tabindex", "-1");
      }

      const container = mapInstance?.getContainer();

      const tabbableElement = container?.parentElement?.parentElement;
      tabbableElement?.setAttribute("tabindex", "-1");

      const mapboxLogo = container.querySelector("a.mapboxgl-ctrl-logo");
      mapboxLogo?.setAttribute("tabindex", "-1");
    }
  };

  // Combined onLoad handler that calls the original onLoad and removes canvas focus
  const handleMapLoad = () => {
    onLoad();
    removeCanvasFocus();
  };

  return (
    <MapboxControlsWrapper>
      {props.shader && (
        <Shader hasFeaturedContent={props.shader.hasFeaturedContent} />
      )}
      <TopLeftMapOverlay halfMap={props.halfMap ?? false}>
        {props.topLeftMapOverlayItems?.map((item, index) => (
          <React.Fragment key={`top-left-map-overlay-item-${index}`}>
            {item}
          </React.Fragment>
        ))}
        {!isDesktop &&
          props.rightPanelOverlayItems?.map((item, index) => (
            <React.Fragment key={`right-panel-overlay-item-${index}`}>
              {item}
            </React.Fragment>
          ))}
      </TopLeftMapOverlay>
      {rightPanelOpen ?
        <RightPanelOverlay>
          {props.rightPanelOverlayItems?.map((item, index) => (
            <React.Fragment key={`right-panel-overlay-item-${index}`}>
              {item}
            </React.Fragment>
          ))}
        </RightPanelOverlay>
      : null}
      {props.leftPanelOverlayItems?.length ?
        isDesktop ?
          <LeftPanelOverlay>
            {props.leftPanelOverlayItems?.map((item, index) => (
              <React.Fragment key={`left-panel-overlay-item-${index}`}>
                {item}
              </React.Fragment>
            ))}
          </LeftPanelOverlay>
        : <MobileBottomTray>
            {props.leftPanelOverlayItems?.map((item, index) => (
              <React.Fragment key={`left-panel-overlay-item-${index}`}>
                {item}
              </React.Fragment>
            ))}
          </MobileBottomTray>

      : null}
      {!rightPanelOpen ?
        <TopRightMapOverlay>
          {props.topRightMapOverlayItems?.map((item, index) => (
            <React.Fragment key={`top-right-map-overlay-item-${index}`}>
              {item}
            </React.Fragment>
          ))}
        </TopRightMapOverlay>
      : null}
      {props.centerMapOverlayItems?.length ?
        <CenterMapOverlay>
          {props.centerMapOverlayItems?.map((item, index) => (
            <React.Fragment key={`center-map-overlay-item-${index}`}>
              {item}
            </React.Fragment>
          ))}
        </CenterMapOverlay>
      : null}
      <Map
        ref={ref}
        cursor={cursor}
        mapboxAccessToken={window.env.MAPBOX_ACCESS_TOKEN}
        initialViewState={initialViewport}
        style={{ width: props.width, height: props.height }}
        minZoom={props.minZoom}
        interactiveLayerIds={interactiveLayerIds}
        attributionControl={false}
        reuseMaps={false}
        onClick={onClick}
        onMouseMove={isDesktop ? onHover : undefined}
        onMouseLeave={isDesktop ? onFocusLost : undefined}
        onLoad={handleMapLoad}
        mapStyle={props.baseMapStyle}
        onError={(e: any) => {
          if (e.error?.status === 401) {
            window.location.href = buildLoginURLWithPrevLocation(location);
          } else if (e.error?.status === 503) {
            onLoadError(e);
          }
        }}
        onMove={onMove}
      >
        {layers}
        {/* the Map Controls are required to be rendered with the Map,
            so the entire BottomRightMapOverlay is rendered here */}
        {!rightPanelOpen ?
          <MapToolProvider onFocusLost={onFocusLost}>
            <BottomRightMapOverlay halfMap={props.halfMap ?? false}>
              {props.bottomRightMapOverlayItems?.map((item, index) => (
                <React.Fragment key={`bottom-right-map-overlay-item-${index}`}>
                  {item}
                </React.Fragment>
              ))}
            </BottomRightMapOverlay>
          </MapToolProvider>
        : null}
        <TooltipComponent tooltip={tooltip} />
      </Map>
    </MapboxControlsWrapper>
  );
};
